import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Download, ArrowRight, QrCode, Zap, Rocket } from "lucide-react";
import { onInstallAvailable, promptInstall, isStandalone } from "../../lib/pwaInstall";

/* Prominent "Open the App" hero — the first thing above the fold on Home.
   Big, editorial, and dead-clear about the two ways to enter the app:
   1) Instant install-to-home-screen (native PWA prompt if the browser supports it)
   2) Open the app-style experience right now at /msp
   Also renders a live QR the user can scan from their phone. */

export default function OpenAppHero() {
  const [installable, setInstallable] = useState(false);
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    setStandalone(isStandalone());
    return onInstallAvailable(setInstallable);
  }, []);

  if (standalone) return null; // already running as installed app — skip the pitch

  const qrTarget = typeof window !== "undefined" ? `${window.location.origin}/msp` : "https://travelerpulse.pro/msp";
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=4&format=png&data=${encodeURIComponent(qrTarget)}`;

  const doInstall = async () => {
    const outcome = await promptInstall();
    if (outcome !== "accepted") {
      // No browser prompt available — fall back to opening the app-style route
      window.location.assign("/msp");
    }
  };

  return (
    <section
      data-testid="home-open-app-hero"
      className="relative overflow-hidden rounded-3xl border border-white shadow-2xl"
    >
      {/* Layered gradient + spotlight */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-[#0b1836] via-[#123064] to-[#1f4bb2]" />
      <div aria-hidden="true" className="absolute -top-24 -right-16 w-[420px] h-[420px] rounded-full bg-amber-300/25 blur-3xl" />
      <div aria-hidden="true" className="absolute -bottom-24 -left-12 w-[380px] h-[380px] rounded-full bg-fuchsia-500/20 blur-3xl" />
      <div aria-hidden="true" className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)",
        backgroundSize: "22px 22px",
      }} />

      <div className="relative grid lg:grid-cols-[1.35fr_1fr] gap-6 lg:gap-10 items-center px-6 sm:px-10 py-10 sm:py-14 text-white">
        {/* LEFT: headline + CTAs */}
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.28em] text-amber-200 bg-white/10 backdrop-blur border border-white/15 rounded-full px-3 py-1">
            <Sparkles className="w-3.5 h-3.5" /> New · My Social Pulse™ App
          </div>
          <h2 className="mt-4 font-serif font-800 text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight drop-shadow-md">
            Your travel life,<br className="hidden sm:block" />
            <span className="italic text-amber-200">one tap away.</span>
          </h2>
          <p className="mt-4 text-blue-50/90 text-base sm:text-lg max-w-xl">
            Trips, hotels, flights, checklists, expenses and a live AI travel assistant — all in a phone-native experience that installs to your home screen in seconds.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={doInstall}
              data-testid="home-open-app-install"
              className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-800 px-6 py-3.5 shadow-lg shadow-amber-400/30 transition"
            >
              <Download className="w-5 h-5" />
              {installable ? "Install the app" : "Open the app"}
            </button>
            <Link
              to="/msp"
              data-testid="home-open-app-preview"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 backdrop-blur text-white font-700 px-5 py-3 transition"
            >
              Try the web app <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-blue-100/85">
            <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-300" /> Instant install, no store required</span>
            <span className="inline-flex items-center gap-1.5"><Rocket className="w-3.5 h-3.5 text-emerald-300" /> Offline-ready shell for saved trips</span>
          </div>
        </div>

        {/* RIGHT: phone silhouette + QR */}
        <div className="hidden lg:flex items-center justify-end gap-6">
          <div className="text-right">
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-amber-200 flex items-center gap-1.5 justify-end">
              <QrCode className="w-3.5 h-3.5" /> Scan from your phone
            </div>
            <div className="text-xs text-blue-100/80 mt-1 max-w-[180px]">
              Opens <span className="font-700 text-white">/msp</span> — the phone-native web app.
            </div>
          </div>
          <div className="bg-white p-2.5 rounded-2xl shadow-xl">
            <img src={qrSrc} alt="Open My Social Pulse on your phone" width={160} height={160} className="block rounded-lg" />
          </div>
        </div>

        {/* QR on mobile — compact row */}
        <div className="lg:hidden flex items-center gap-3 border-t border-white/15 pt-5 -mx-1">
          <div className="bg-white p-2 rounded-xl shadow-lg">
            <img src={qrSrc} alt="Open on phone" width={84} height={84} className="block rounded" />
          </div>
          <div className="text-xs text-blue-100/85">
            <div className="text-white font-700 text-sm">Scan to open on your phone</div>
            <div className="mt-0.5">The web app runs in Safari and Chrome — add to home screen for a native feel.</div>
          </div>
        </div>
      </div>
    </section>
  );
}
